import { adminApi, adminApiInZone } from './api-client';
import type { PortalZone } from './zones';

/**
 * Route one call to a SPECIFIC zone.
 *
 * Every route below is served out of ONE zone's Tinybird workspace (ADR-120 Stage 4 proved the
 * two workspaces are SEPARATE, matched-region stores). So no figure here is ever SUMMED across
 * zones — a QoS baseline added to another zone's would belong to neither jurisdiction.
 *
 * ⭐ WHAT CHANGED ON 13 OCTOBER 2026 (Jeff's rule, 11 October, confirmed 13 October): the Flow
 * Validator is a READ-ONLY OPS BOARD, and read-only boards are ZONE-AGNOSTIC — both zones shown,
 * a Zone column, a per-zone legend, a filter defaulting to "All zones". The page therefore calls
 * each function below once PER ZONE (`fanOutZones`, from the browser — ADR-120 Amendment 2) and
 * shows the rows side by side, each tagged with its zone. That is an interleave of labelled
 * rows, never a merge of figures: every session, event, report and domain row is still one
 * zone's record, read from that zone's store, and says which zone it is. (Supersedes the earlier
 * ADR-122 Pattern 3 "pick one zone" treatment of this page.) Actions on a row — view, retry,
 * regenerate — are routed to the row's own zone.
 *
 * ⚠ OMITTING THE ZONE MEANS "HOME", NOT "ALL" — same convention as `customers-api.ts`.
 */
const call = <T>(zone: PortalZone | undefined, path: string, init?: RequestInit): Promise<T> =>
  zone ? adminApiInZone<T>(zone, path, init) : adminApi<T>(path, init);

/**
 * Typed wrappers for the Telemetry / Flow Validator surface (Stage 10, #50,
 * ADR-058). The page mounts at /admin/diags; the admin-api routes are
 * /admin/diags/{events,report-cache,analyse}. Server role gate:
 * MUTATING_ROLES (nexa_admin + super_admin).
 *
 * Both views key on `domainToken` — the join key the events datasource and the
 * (DOMAIN-keyed) ReportCache table actually share. ReportCache carries no
 * accountId, so there is no accountId filter (ADR-058 D4).
 */

export type Channel = 'paid' | 'email' | 'social' | 'organic_search' | 'referral' | 'direct';

export interface TelemetryEvent {
  timestamp: string;
  domainToken: string;
  /** Human-readable hostname resolved server-side from the Domain table (source
   *  of truth). '' when the token isn't registered — render as `unknown`. */
  hostname: string;
  pageUrl: string;
  referrer: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  sessionToken: string;
  deviceType: string;
  geoCountry: string;
  geoRegion: string;
  pageTitle: string;
  asn: number;
  asOrganization: string;
  isBot: number;
  /**
   * Write-time agent taxonomy (ADR-103 D1), classified at the edge from the
   * User-Agent cross-checked against the network's AS-organisation.
   * `agentClass` ∈ human | llm | crawler | synthetic; `agentName` is a vendor
   * slug (claudebot, googlebot, embed-probe, …) or '' when unidentifiable.
   *
   * BOTH ARE '' ON HISTORICAL ROWS and can never be backfilled — the
   * User-Agent that would have identified them was read at the edge and
   * discarded. Such a row renders as "Legacy (untagged)"; we do not infer a
   * class we cannot know.
   */
  agentClass: string;
  agentName: string;
  /** Event provenance (Stage 10 three-value taxonomy): `live` (real browser
   *  snippet), `synthetic` (deliberate `X-Nexa-Synthetic` test traffic), or
   *  `beta` (pre-launch build/probe rows, backfilled from the legacy DEFAULT). */
  source: string;
  /** Ingest event type: `pageview` (default), `dwell`, `qos`, `journey`
   *  (signup-funnel breadcrumbs), or `interaction` (ADR-095 auto-capture). */
  eventType: string;
  /** Measured seconds-on-page (dwell events only; 0 otherwise). */
  dwellSeconds: number;
  /** Free-form JSON blob. For `journey` events it carries `{step, ...}`; for
   *  `interaction` the element fingerprint; for `qos`/`dwell` the sample; and
   *  the `inferredSource` UA marker on a pageview. '' for a plain pageview. */
  meta: string;
  /** Derived server-side: the SESSION acquisition channel (ADR-047 §3),
   *  classified from the session's entry pageview and inherited by later
   *  in-site pageviews. */
  channel: Channel;
  /** True for the earliest event of each sessionToken in the loaded window
   *  (the landing page) — drives the `entry` chip. */
  isEntry: boolean;
  /** The zone this row came from — tagged by the client after the per-zone fan-out. */
  zone?: string;
}

/** The resolved acquisition-source identity for a session (ADR-095) — drives
 *  the SourceChip (brand glyph + name). */
export interface SourceIdentity {
  /** `llm` / `crawler` (ADR-103 D1) are MACHINE identities — a named bot vendor
   *  rather than an acquisition source. They are resolved from the write-time
   *  agentClass/agentName, not from a referrer, which is why they sit in the
   *  same union: the identity column answers "who is this?" for every row. */
  kind: 'synthetic' | 'llm' | 'crawler' | 'referrer' | 'utm' | 'inference' | 'referral' | 'direct';
  /** Curated platform slug for a brand glyph (linkedin/instagram/facebook/x/
   *  youtube/tiktok/google/reddit/pinterest/snapchat/probe), '' when none. */
  platform: string;
  /** Human label (a platform name, referrer host, referral code, or 'Direct'). */
  label: string;
  /** True when resolved from in-app UA inference — renders the '· in-app
   *  (inferred)' sub-label. */
  inferred: boolean;
}

/** One ordered step in a session's path timeline. */
export interface SessionStep {
  timestamp: string;
  eventType: string;
  /** Path-only page (`/plans`, `/partners#apply`); '' for a non-page event. */
  pagePath: string;
  /** Full page URL (hover/title). */
  pageUrl: string;
  isEntry: boolean;
  /** Dwell seconds joined onto a pageview step (0 if none) — drives the dwell chip. */
  dwellSeconds: number;
  meta: string;
  /** Journey funnel step ('' for non-journey events). */
  journeyStep: string;
}

/** A grouped session — one visitor's ordered path within the fetched window. */
export interface TelemetrySession {
  /** Unique row identity: `sessionToken#<visit entry timestamp>`. One browser
   *  tab's sessionToken can split into several visits (30-min idle rule), so
   *  the raw token is NOT unique — use this for React keys and expand state. */
  sessionId: string;
  sessionToken: string;
  domainToken: string;
  hostname: string;
  startedAt: string;
  /** Timestamp of the session's most recent event — the list is ordered by
   *  this, so an active session stays at the top however long ago it began. */
  lastActivityAt: string;
  source: string;
  isBot: boolean;
  /** The session's agent taxonomy, taken from its ENTRY event — the same rule
   *  `channel` and `identity` follow. A session is one visitor's ordered path,
   *  so its class cannot change mid-session. '' on historical rows. */
  agentClass: string;
  agentName: string;
  channel: Channel;
  identity: SourceIdentity;
  pageCount: number;
  interactionCount: number;
  durationSeconds: number;
  /** Distinct sessionTokens this visit was stitched from (D2). > 1 means several
   *  tokens were reunited via the shared visitorId. WHY they fragmented is
   *  `inApp`, not this count — see below. */
  fragmentCount: number;
  /** True when an event in this visit carried a known in-app browser UA
   *  (`meta.inferredSource`) — the only signal that distinguishes real in-app
   *  per-page fragmentation from ordinary multi-tab browsing. A sessionToken is
   *  per-TAB, so `fragmentCount > 1` on its own just means "more than one tab".
   *  Absent/false on rows served before 26 July 2026. */
  inApp?: boolean;
  steps: SessionStep[];
  /** The zone this row came from — tagged by the client after the per-zone fan-out. */
  zone?: string;
}

export interface TelemetryEventsResponse {
  events: TelemetryEvent[];
  /** Grouped Sessions view (ADR-095) — computed server-side, added additively. */
  sessions: TelemetrySession[];
  count: number;
  /** false when the Tinybird admin token isn't provisioned (ops gap, not error). */
  configured: boolean;
  /** echoes the server-applied recent-time window (minutes). */
  windowMinutes?: number;
  /**
   * O2 (11 October 2026) — present ONLY when the window is empty: the zone's most recent beacon
   * of any kind, so the page can say "Last event <time> — widen the window" instead of looking
   * broken. Absent when the window has events, when Tinybird is unconfigured, or when the
   * workspace holds nothing at all.
   */
  lastEventAt?: string;
  /** present when Tinybird was reachable-but-failing or unreachable. */
  error?: string;
}

export interface ReportCacheRecord {
  domainToken: string;
  /** Resolved hostname (source of truth); '' when unregistered — render as `unknown`. */
  hostname: string;
  reportMonth: string;
  status: string;
  generatedAt: string;
  schemaVersion: number | null;
  sections: { scorecard: string; attribution: string; intelligence: string } | null;
  trigger: string | null;
  /** Server-authoritative: status='generating' AND started > 15 min ago (the
   *  pipeline died mid-run). Drives the status-aware badge + stuck-row + Retry. */
  isStuck: boolean;
  /** The zone this row came from — tagged by the client after the per-zone fan-out. */
  zone?: string;
}

export interface ReportCacheResponse {
  records: ReportCacheRecord[];
  count: number;
}

export interface ReportViewResponse {
  domainToken: string;
  reportMonth: string;
  status: string;
  generatedAt: string;
  trigger: string | null;
  /** The exact stored reportJson the subscriber dashboard reads; null when the
   *  record exists but has no assembled report yet (generating/failed). */
  reportJson: Record<string, unknown> | null;
}

export interface AnalyseResponse {
  narrative: string | null;
  /** false when the Anthropic API key isn't provisioned. */
  configured: boolean;
  snapshot?: unknown;
}

function domainTokenQuery(domainToken?: string): string {
  const t = domainToken?.trim();
  return t ? `domainToken=${encodeURIComponent(t)}` : '';
}

/**
 * Fetch recent events within a time window (Stage 10 UAT §D). The server bounds
 * the read to the last `windowMinutes` (default 60 = last 1h) and returns up to
 * `limit` rows (Beta-scale fetch cap); the page filters those client-side by
 * hostname/token + source and renders only the newest 50.
 */
export function getTelemetryEvents(
  windowMinutes = 60,
  limit = 500,
  /** The zone whose Tinybird workspace to read. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<TelemetryEventsResponse> {
  const parts = [`windowMinutes=${windowMinutes}`, `limit=${limit}`];
  return call<TelemetryEventsResponse>(zone, `/admin/diags/events?${parts.join('&')}`);
}

/** Newest ReportCache records across all domains; the page filters client-side
 *  by the shared search box (kickoff §B), so no server-side token filter. */
export function getTelemetryReportCache(
  /** The zone to read. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<ReportCacheResponse> {
  return call<ReportCacheResponse>(zone, '/admin/diags/report-cache');
}

// ── QoS domain baseline (ADR-093 Stage 6b) — Performance tab ──

export interface QosBaselineRow {
  domainToken: string;
  hostname: string;
  samples: number;
  attempts: number;
  successes: number;
  /** successes / attempts, 0..1. */
  availability: number;
  rttP50: number;
  rttP95: number;
  jitter: number;
  meanRetries: number;
  ct4g: number;
  ct3g: number;
  ct2g: number;
  ctSlow2g: number;
  ctUnknown: number;
  /** Send-time distribution for the gauge count badges (ADR-110): sends whose
   *  time fell in each band (fast <100ms · ok <500ms · slow <2s · very-slow ≥2s).
   *  The four sum to the total sends the gauge shows. 0 before the additive
   *  qos_domain_baseline columns deploy (the gauge degrades to the marker only). */
  bandFast: number;
  bandOk: number;
  bandSlow: number;
  bandVerySlow: number;
  /** The zone this row came from — tagged by the client after the per-zone fan-out. */
  zone?: string;
}

export interface QosResponse {
  rows: QosBaselineRow[];
  count: number;
  /** false when the Tinybird admin token isn't provisioned (ops gap, not error). */
  configured: boolean;
  /** echoes the server-applied window (hours: 24 | 168 | 720). */
  windowHours?: number;
  /** present on a degraded read: 'query_budget_guard' | 'tinybird_http_*' | 'tinybird_unreachable'. */
  error?: string;
}

/** Per-domain channel QoS baselines for a rolling window (24h/7d/30d). The page
 *  loads the whole window and filters/sorts client-side (Jeff, 11 July 2026). */
export function getTelemetryQos(
  windowHours = 24,
  /** The zone to read. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<QosResponse> {
  return call<QosResponse>(zone, `/admin/diags/qos?windowHours=${windowHours}`);
}

// ── QoS domain trend (telemetry-uat2) — daily RTT p5/p50/p95 for the sparkline ──

export interface QosTrendRow {
  domainToken: string;
  hostname: string;
  /** UTC calendar day, `YYYY-MM-DD`. */
  day: string;
  samples: number;
  rttP5: number;
  rttP50: number;
  rttP95: number;
  /** The zone this row came from — tagged by the client after the per-zone fan-out. */
  zone?: string;
}

export interface QosTrendResponse {
  rows: QosTrendRow[];
  count: number;
  configured: boolean;
  windowHours?: number;
  error?: string;
}

/** Per-domain per-day RTT trend (p5/p50/p95) for the Performance-tab sparkline.
 *  Defaults to a 7-day window so the sparkline has several daily points. */
export function getTelemetryQosTrend(
  windowHours = 168,
  /** The zone to read. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<QosTrendResponse> {
  return call<QosTrendResponse>(zone, `/admin/diags/qos-trend?windowHours=${windowHours}`);
}

// ── Telemetry Health (ADR-103 D6 / ADR-110) — Section 1 of the Performance tab ──

/** One hour of the Arrival Rhythm — beacons received per hour (`hour` is the
 *  ISO start-of-hour). The SVG draws these so silence is visible as bars stopping. */
export interface ArrivalHour {
  hour: string;
  beacons: number;
}

export interface TelemetryHealthRow {
  domainToken: string;
  hostname: string;
  beaconsInWindow: number;
  /** ISO timestamp of the most recent beacon, '' if none in window. */
  lastSeen: string;
  secondsSinceLastSeen: number;
  /** The domain's OWN median inter-arrival gap — the basis for silence. */
  typicalGapSeconds: number;
  /** 'ok' | 'silent' | 'unknown'. `unknown` fails OPEN — never rendered as silent. */
  health: string;
  /** Arrival Rhythm series (beacons/hour). `[]` until the telemetry_arrival_rhythm
   *  pipe is deployed — the chart degrades to an empty rhythm. */
  arrivals: ArrivalHour[];
  /** The zone this row came from — tagged by the client after the per-zone fan-out. */
  zone?: string;
}

export interface TelemetryHealthResponse {
  rows: TelemetryHealthRow[];
  count: number;
  /** Number of domains with health='silent' (the header badge). */
  silent: number;
  configured: boolean;
  windowHours?: number;
  silenceMultiplier?: number;
  error?: string;
}

/** Per-domain telemetry health (Arrival Rhythm + silence verdict). Defaults to a
 *  7-day window so a low-traffic domain has a measurable rhythm. */
export function getTelemetryHealth(
  windowHours = 168,
  /** The zone to read. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<TelemetryHealthResponse> {
  return call<TelemetryHealthResponse>(zone, `/admin/diags/telemetry-health?windowHours=${windowHours}`);
}

// ── Visitor Experience (ADR-103 D7 / ADR-110) — Section 3 of the Performance tab ──

export interface VisitorExperienceRow {
  domainToken: string;
  hostname: string;
  /** 'mobile' | 'desktop' | 'tablet' | '' (unknown). */
  deviceType: string;
  samples: number;
  /** Median full page-load (ms). The UI labels it "typical", never p50. */
  typicalLoadMs: number;
  slowLoadMs: number;
  typicalTtfbMs: number;
  typicalDomMs: number;
  pages: number;
  /** The zone this row came from — tagged by the client after the per-zone fan-out. */
  zone?: string;
}

export interface VisitorExperienceResponse {
  rows: VisitorExperienceRow[];
  count: number;
  configured: boolean;
  windowHours?: number;
  error?: string;
}

/** Per-domain per-device page-load timing (the cross of device mix × load).
 *  Defaults to a 30-day window (history builds slowly — nav-timing accrues from
 *  the taxonomy deploy forward). */
export function getVisitorExperience(
  windowHours = 720,
  /** The zone to read. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<VisitorExperienceResponse> {
  return call<VisitorExperienceResponse>(zone, `/admin/diags/visitor-experience?windowHours=${windowHours}`);
}

export function analyseTelemetry(
  domainToken?: string,
  /** The zone this analysis runs against. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<AnalyseResponse> {
  const q = domainTokenQuery(domainToken);
  return call<AnalyseResponse>(zone, `/admin/diags/analyse${q ? `?${q}` : ''}`, {
    method: 'POST',
  });
}

/** Fetch the stored reportJson for a domain+month (decision 7) — the same object
 *  the subscriber dashboard reads, rendered staff-side in the report viewer. */
export function getTelemetryReport(
  domainToken: string,
  month: string,
  /** The zone to read. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<ReportViewResponse> {
  return call<ReportViewResponse>(zone,
    `/admin/diags/report/${encodeURIComponent(domainToken)}/${encodeURIComponent(month)}`,
  );
}

/** Re-queue a report-generate run for a domain+month via the staff_regenerate
 *  path (decision 5/6) — serves both the Retry (stuck) and Regenerate (complete)
 *  actions. 202 on enqueue. */
export function retryTelemetryReport(
  domainToken: string,
  month: string,
  /** The zone this re-queue targets. Omitted = the home zone. */
  zone?: PortalZone,
  /** ADR-131 — 2 regenerates with Website scoring v2 (staff only, uncapped); omitted = the platform default. */
  engineVersion?: 1 | 2,
): Promise<{ enqueued: boolean; domainToken: string; reportMonth: string; engineVersion?: 1 | 2 }> {
  return call(zone,
    `/admin/diags/report/${encodeURIComponent(domainToken)}/${encodeURIComponent(month)}/retry`,
    { method: 'POST', ...(engineVersion ? { body: JSON.stringify({ engineVersion }) } : {}) },
  );
}
